// Web Push helpers: register the service worker and manage THIS device's
// subscription. Everything degrades gracefully — unsupported browsers or a
// missing VAPID key just report a state, they never throw at import time.

const vapidKey = () => document.querySelector('meta[name="vapid-public-key"]')?.content
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

export function pushSupported() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !!vapidKey()
  )
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register('/service-worker')
  } catch (e) {
    console.error('Service worker registration failed', e)
    return null
  }
}

// VAPID public key is base64url; PushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

export async function currentSubscription() {
  if (!pushSupported()) return null
  const reg = await navigator.serviceWorker.ready
  return reg.pushManager.getSubscription()
}

// Ask permission, subscribe, and hand the subscription to the server.
export async function enablePush() {
  if (!pushSupported()) throw new Error('unsupported')
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('denied')

  const reg = await navigator.serviceWorker.ready
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey()),
    }))

  const { keys } = sub.toJSON()
  const res = await fetch('/push_subscriptions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ endpoint: sub.endpoint, p256dh: keys.p256dh, auth: keys.auth }),
  })
  if (!res.ok) throw new Error('save-failed')
  return sub
}

// Unsubscribe this device and tell the server to forget it.
export async function disablePush() {
  const sub = await currentSubscription()
  if (!sub) return
  const { endpoint } = sub
  await sub.unsubscribe()
  await fetch('/push_subscriptions', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ endpoint }),
  })
}
